import React, { useEffect, useState } from 'react';
import API from '../../services';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { setBrowser, setIsLoggedIn } from '../../store/authReducer';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { IonLabel } from '@ionic/react';
import { useTranslation } from '../../hooks/useTranslation';

interface PasswordFormProps {
    userData: {
        mobileNumber: string;
        countryCode: string;
        password: string;
        otpToken: string;
    };
    updateUserData: (data: any) => void;
    timeRemaining: number;
    setTimeRemaining: React.Dispatch<React.SetStateAction<number>>;
    goToPreviousStep: () => void;
    goToLoginStep: () => void;
}

const PasswordForm: React.FC<PasswordFormProps> = ({
    userData,
    updateUserData,
    timeRemaining,
    setTimeRemaining,
    goToPreviousStep,
    goToLoginStep
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: '',
    });
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        // Set up timer
        const interval = setInterval(() => {
            setTimeRemaining(prev => {
                if (prev <= 1) {
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [setTimeRemaining]);



    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setLoading(true);
        try {
            const response = await API.post(`/auth/signup`, {
                token: userData.otpToken,
                password: formData.password,
                userId: "",
                promoCode: "",
                role: "USER"
            });

            const data = await response.data;
            if (data.status !== "RS_OK") {
                toast.error(`${data.errorDescription}`);
            } else {
                if (data.status === "RS_OK" && data.userStatus=== "BLOCKED") {
                    sessionStorage.setItem("user_blocked", "true");
                }
                sessionStorage.setItem("userKey", response?.data?.userKey)
                sessionStorage.setItem("token", response?.data?.accessToken);
                sessionStorage.setItem("rtoken", response?.data?.refreshToken);
                dispatch(setIsLoggedIn(true));
                dispatch(setBrowser("Web"));
                toast.success(t('auth.messages.loggedInSuccessfully'));
            }
        } catch (error) {
            console.error("There was a problem with the fetch operation:", error);
        } finally {
            setLoading(false);
        }
        navigate("/home")
    };

    const validatePassword = (password: any) => {
        if (!password) return t('auth.messages.passwordIsRequired');

        const hasCapital = /[A-Z]/.test(password);
        const hasSmallLetter = /[a-z]/.test(password);
        const hasNumber = /\d/.test(password);
        // const hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(password);
        const hasMinLength = password.length >= 8;

        if (!hasCapital || !hasSmallLetter || !hasNumber || !hasMinLength) {
            return t('auth.messages.passwordMustBeAtLeast8Characters');
        }

        return null; // Valid
    };

    const handleChange = (e: any) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        let newErrors = { ...errors };

        if (name === 'password') {
            // Use your custom validator
            const passwordError = validatePassword(value);
            if (passwordError) {
                newErrors.password = passwordError;
            } else {
                delete newErrors.password;
            }

            // Also validate confirmPassword if it has value
            if (formData.confirmPassword && value !== formData.confirmPassword) {
                newErrors.confirmPassword = t('auth.messages.passwordsDoNotMatch');
            } else {
                delete newErrors.confirmPassword;
            }
        }

        if (name === 'confirmPassword') {
            if (value !== formData.password) {
                newErrors.confirmPassword = t('auth.messages.passwordsDoNotMatch');
            } else {
                delete newErrors.confirmPassword;
            }
        }

        setErrors(newErrors);
    };

    return (
        <>
            <div>
                <div className="flex items-center justify-between mb-4">
                    <label className="text-sm text-zinc-400">
                        {t('auth.mobileNumber')}
                    </label>
                    <div className="flex items-center">
                        <span className="text-sm text-white">+{userData.countryCode} {userData.mobileNumber}</span>
                    </div>
                </div>

                <div className="mb-4 text-center">
                    <p className="text-xs text-zinc-400 mt-1">
                        {timeRemaining > 0
                            ? t('auth.timeRemaining').replace('{time}', `${Math.floor(timeRemaining / 60)}:${(timeRemaining % 60).toString().padStart(2, '0')}`)
                            : t('auth.timeExpired')}
                    </p>
                </div>

                <form className="flex flex-col w-full" onSubmit={handleSubmit} autoComplete="off">
                    <h2 className="text-2xl font-semibold text-white mb-2">{t('auth.resetPassword')}</h2>
                    <p className="text-sm text-zinc-400 mb-4">{t('auth.createYourPasswordByFollowingTheseSimpleSteps')}</p>


                    <IonLabel className="text-sm text-zinc-400 mt-4 mb-1">
                        {t('auth.password')} <span className="text-red-500">*</span>
                    </IonLabel>
                    <div className="relative w-full">
                        <input
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            placeholder={t('auth.password')}
                            value={formData.password}
                            onChange={handleChange}
                            className={`p-2 pr-10 w-full border rounded-md text-sm bg-zinc-800 text-white border-zinc-700 focus:outline-none focus:ring-2 focus:ring-primary ${errors.password ? 'border-red-500' : ''}`}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword((prev) => !prev)}
                            className="absolute inset-y-0 right-2 flex items-center text-white"
                        >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                        </button>
                    </div>
                    {errors.password && (
                        <span className="text-red-500 text-xs mt-1">{errors.password}</span>
                    )}

                    {/* Confirm Password */}
                    <IonLabel className="text-sm text-zinc-400 mt-4 mb-1">
                        {t('auth.confirmPassword')} <span className="text-red-500">*</span>
                    </IonLabel>
                    <div className="relative w-full">
                        <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            name="confirmPassword"
                            placeholder={t('auth.confirmPassword')}
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            className={`p-2 pr-10 w-full border rounded-md text-sm bg-zinc-800 text-white border-zinc-700 focus:outline-none focus:ring-2 focus:ring-primary ${errors.confirmPassword ? 'border-red-500' : ''}`}
                        />
                        <button
                            type="button"
                            onClick={() => setShowConfirmPassword((prev) => !prev)}
                            className="absolute inset-y-0 right-2 flex items-center text-white"
                        >
                            {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                        </button>
                    </div>
                    {errors.confirmPassword && (
                        <span className="text-red-500 text-xs mt-1">{errors.confirmPassword}</span>
                    )}

                    {/* Submit Button */}
                    <div className="flex justify-between mt-6">
                        <button
                            type="button"
                            onClick={goToLoginStep}
                            className="px-4 py-2 text-sm text-white bg-zinc-700 rounded-md hover:bg-zinc-600 transition-colors"
                        >
                            {t('common.back')}
                        </button>

                        <button
                            type="submit"
                            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center"
                            disabled={loading || !formData.password || !formData.confirmPassword || (formData.password !== formData.confirmPassword) || timeRemaining <= 0}
                        >
                            {loading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {t('auth.loggingIn')}
                                </>
                            ) : (
                                t('auth.login')
                            )}
                        </button>
                    </div>
                </form>

            </div>
        </>
    );
};

export default PasswordForm;